import useTranslation from 'next-translate/useTranslation';
import { ContestStatus, ContestType } from '@betterpool/api-types/contests-service';
import { useRouter } from 'next/router';
import { Group } from '@mantine/core';
import { memo, useContext, useMemo } from 'react';

import repeat from '~/common/utils/repeat';
import { getCanMakePicks } from '~/domains/contest/domains/common/utils/pickControl';
import type { Entry } from '~/domains/contest/domains/game/domains/common/types/entry';
import { PickVariant } from '~/domains/contest/domains/game/domains/common/types/single.pick';
import type { UserEntry } from '~/domains/contest/domains/services/interfaces/user-entry.interface';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';
import type { TeamPick } from '~/domains/contest/domains/services/interfaces/team-picks.interface';
import { Match } from '~/domains/common/components/Match/Match';
import { Routes } from '~/domains/common/constants/routes';
import type { Slate } from '~/domains/games/interfaces/slates.interface';
import { getPicksCount } from '~/domains/contest/domains/common/utils/getPicksCount';
import { useIsEntryWithdrawalAllowed } from '~/domains/contest/domains/common/hooks/useIsEntryWithdrawalAllowed';
import {
  GroupEntryViewsStoreContext,
  useGroupEntryViewSelector,
} from '~/domains/group/state/groupEntryViews.store';
import { getSingleSlates } from '../../../../../game/domains/common/utils/getSingleSlates';
import { GamePick } from '../../../../../game/domains/common/components/GamePick/GamePick';
import { Pick } from '../../../../../game/domains/common/components/Pick/Pick';
import EntryRow from '../Entry/EntryRow';

type MyEntriesContentProps = {
  entries?: UserEntry[];
  fullContest: FullContest;
  defaultSlate: Slate;
  teamPicks: TeamPick;
  openEntryNameEditModal: (entryInfo: { id: string; name: string }) => void;
};

type EntryRowContentProps = {
  entry: Entry;
  fullContest: FullContest;
  teamPicks: TeamPick;
  contestType: ContestType | undefined;
  isScheduled: boolean;
  hasGroupEntryViewsStore: boolean;
  isEntryWithdrawalAllowed: boolean;
  openEntryNameEditModal: (entryInfo: { id: string; name: string }) => void;
};

const EntryRowContent = memo(({
  entry,
  fullContest,
  teamPicks,
  contestType,
  isScheduled,
  hasGroupEntryViewsStore,
  isEntryWithdrawalAllowed,
  openEntryNameEditModal,
}: EntryRowContentProps) => {
  const { t } = useTranslation('contest');
  const router = useRouter();
  const view = useGroupEntryViewSelector(entry.id);
  // A viewer needing masking always holds a share, so isMultiOwner gates all masking.
  const isOwner =
    entry.isMultiOwner !== true || (hasGroupEntryViewsStore && view?.viewerRole === 'organizer');
  const canMakePicks = getCanMakePicks(entry);

  const getPicksheetHref = (slateId?: string) =>
    Routes.gamecenterPicks(fullContest?.id, {
      entryId: entry.id,
      slateId,
    });

  return (
    <EntryRow
      name={
        !hasGroupEntryViewsStore || isOwner
          ? entry.name
          : t('gamecenter.common.entryScheduled.groupEntry')
      }
      entryId={entry.id}
      contestId={fullContest?.id}
      isEntertainmentOnly={fullContest?.settings?.entertainmentOnlySettings?.enabled}
      options={
        isOwner || canMakePicks
          ? {
              ...(canMakePicks && {
                editName: {
                  title: t('detail.entries.entry.option.editName'),
                  onClick: () => openEntryNameEditModal({ id: entry.id, name: entry.name }),
                },
              }),
              ...(isOwner && {
                withdrawal: {
                  title: t('detail.entries.entry.option.withdraw'),
                  isHidden: !isEntryWithdrawalAllowed,
                },
              }),
              ...(canMakePicks && {
                edit: {
                  title: entry.isEmpty
                    ? t('detail.entries.entry.option.make')
                    : t('detail.entries.entry.option.edit'),
                  href: getPicksheetHref(),
                },
              }),
            }
          : undefined
      }
    >
      <Match value={contestType}>
        <Match.On value={[ContestType.TEAM_SURVIVOR]}>
          <Group noWrap /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4}>
            {entry.singleSlates.map((singleSlate, index) => (
              <Pick
                key={singleSlate.id}
                onSlateClick={
                  canMakePicks
                    ? (slateId) => {
                        router.push(getPicksheetHref(slateId));
                      }
                    : undefined
                }
                shouldMakePicks={
                  canMakePicks &&
                  isScheduled &&
                  teamPicks?.[entry.id]?.picks &&
                  !Object.keys(teamPicks?.[entry.id]?.picks).length &&
                  index === 0 &&
                  entry?.currentSingleSlate?.isGenerated
                }
                variant={PickVariant.MY_ENTRIES}
                {...singleSlate}
              />
            ))}
          </Group>
        </Match.On>

        <Match.On value={[ContestType.TEAM_PICK_EM]}>
          <Group noWrap /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4}>
            {entry?.currentSingleSlate?.picks?.map((pick) => (
              <GamePick
                key={pick.teamId}
                {...pick}
                isDisabled={!canMakePicks}
                onGameClick={
                  canMakePicks
                    ? () => {
                        router.push(getPicksheetHref());
                      }
                    : undefined
                }
              />
            ))}

            {repeat(getPicksCount(entry?.currentSingleSlate), (index) => (
              <GamePick
                key={index}
                isDisabled={!canMakePicks}
                shouldMakePicks={
                  canMakePicks &&
                  isScheduled &&
                  teamPicks?.[entry.id]?.picks &&
                  !Object.keys(teamPicks?.[entry.id]?.picks).length &&
                  index === 0 &&
                  entry?.currentSingleSlate?.isGenerated
                }
                onGameClick={
                  canMakePicks
                    ? () => {
                        router.push(getPicksheetHref());
                      }
                    : undefined
                }
              />
            ))}
          </Group>
        </Match.On>
      </Match>
    </EntryRow>
  );
});
EntryRowContent.displayName = 'EntryRowContent';

function MyEntriesContent({
  fullContest,
  teamPicks,
  defaultSlate,
  entries = [],
  openEntryNameEditModal,
}: MyEntriesContentProps) {
  const groupEntryViewsStore = useContext(GroupEntryViewsStoreContext);

  const contestType = fullContest?.contest_type;
  const contestStatus = fullContest?.status;
  const isScheduled = contestStatus === ContestStatus.SCHEDULED;
  const { isEntryWithdrawalAllowed } = useIsEntryWithdrawalAllowed({ fullContest });

  const userEntries: Entry[] = useMemo(
    () =>
      entries.map((entry) => {
        const { singleSlates, currentSingleSlate } = getSingleSlates({
          slates: fullContest?.slates,
          currentTeamPick: teamPicks?.[entry.id],
          currentUserEntry: entry,
          settings: fullContest?.settings,
          leagueAlias: fullContest?.league?.alias,
          selectedSlateId: defaultSlate?.id,
        });
        return {
          id: entry.id,
          name: entry.name,
          singleSlates,
          currentSingleSlate,
          isEmpty: !teamPicks?.[entry.id],
          isPickController: entry.isPickController,
          isMultiOwner: entry.isMultiOwner,
        };
      }),
    [defaultSlate?.id, entries, fullContest, teamPicks]
  );

  return (
    <>
      {userEntries.map((entry) => (
        <EntryRowContent
          key={entry.id}
          entry={entry}
          fullContest={fullContest}
          teamPicks={teamPicks}
          contestType={contestType}
          isScheduled={isScheduled}
          hasGroupEntryViewsStore={groupEntryViewsStore !== null}
          isEntryWithdrawalAllowed={isEntryWithdrawalAllowed}
          openEntryNameEditModal={openEntryNameEditModal}
        />
      ))}
    </>
  );
}

export default MyEntriesContent;
